import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

export interface User {
  id: number;
  username: string;
  name: string;
  profileImage: string;
  role: string;
  sellerType: string;
  location: string;
  currentBelt: string;
  totalPoints: number;
  createdAt: string;
  modifiedAt: string;
  distributorCode: string;
  distributorName: string;
}

interface UserContextType {
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  logout: () => void;
  isAuthenticated: boolean;
  hasRole: (role: string | string[]) => boolean;
  loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored token and validate it
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      validateToken();
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async () => {
    setLoading(true);
    try {
      const response = await authService.validateToken();
      setCurrentUser(response.user);
      setIsAuthenticated(true);
    } catch (error) {
      // If token is invalid, clear it
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      setCurrentUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const setUser = (user: User | null) => {
    setCurrentUser(user);
    setIsAuthenticated(!!user);
  };

  const logout = async () => {
    await authService.logout();
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  const hasRole = (role: string | string[]) => {
    if (!currentUser) return false;
    if (Array.isArray(role)) {
      return role.includes(currentUser.role);
    }
    return currentUser.role === role;
  };

  return (
    <UserContext.Provider value={{ currentUser, setCurrentUser: setUser, logout, isAuthenticated, hasRole, loading }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
} 